import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import QuestionMarkIcon from '@ui/Icon/QuestionMark';
import Typography from '@ui/Typography';
import Tag from '@ui/Tag';
import ModelOption from './ModelOption';
import SingleSelect from '../../../DropdownMenu/menus/SingleSelect';
import { ModelOptionProps } from './types';
import {
  AZURE_VERSION_PLACEHOLDER,
  VersionSubOption,
} from '../hooks/useProjectVersion';

export type OtherLanguagesOption = VersionSubOption;

export interface OtherLanguagesOptionProps
  extends Pick<ModelOptionProps, 'disabled' | 'active'> {
  options: Array<OtherLanguagesOption>;
  error?: boolean;
  isUpgrade?: boolean;
  /** When false, hides the “Retiring” pill on the Legacy Global card title. */
  selectedStudioVersion?: OtherLanguagesOption['id'] | string | null;
  onChange: (selected: OtherLanguagesOption) => void;
}

const HELP_LINK = `https://www.wellsaid.io/contact-sales`;

const HelpIcon = () => {
  return (
    <QuestionMarkIcon
      color="white"
      ctaPlacement="prefix"
      ctaText={'Contact us'}
      text={'to add languages to your subscription.'}
      href={HELP_LINK}
      placement="bottom-start"
      contentWidth={150}
      PopperProps={{ style: { marginTop: -10 } }}
    />
  );
};

const useStyles = makeStyles()(theme => ({
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  withHelpIconContainer: {
    display: 'flex',
    alignItems: 'center',
    '& > button': { zIndex: 1 },
  },
  inputContainer: {
    display: 'grid',
    width: '100%',
    gap: 10,
    '& > button': {
      color: 'inherit !important',
      '& > span > *': { color: 'inherit' },
    },
  },
  disabled: { opacity: 0.5 },
  inactive: { color: palette.gray_75 },
  active: { color: palette.gray_0 },
}));

const OtherLanguagesOption = ({
  active,
  options,
  disabled,
  error,
  isUpgrade = false,
  selectedStudioVersion,
  onChange,
}: OtherLanguagesOptionProps) => {
  const filteredOptions = React.useMemo(
    () => options.filter(o => !o.disabled),
    [options]
  );

  const selection = React.useMemo(() => {
    if (
      !selectedStudioVersion ||
      selectedStudioVersion === AZURE_VERSION_PLACEHOLDER
    )
      return 'Select';
    const match = filteredOptions.find(o => o.id === selectedStudioVersion);
    return match?.label ?? 'Select';
  }, [selectedStudioVersion, filteredOptions]);

  const showError = !disabled && !!error && selection === 'Select';

  const handleSelectionChange = (value: string) => {
    const selectedOption = filteredOptions.find(o => o.label === value);
    if (selectedOption) onChange(selectedOption);
  };

  const { classes, cx } = useStyles();

  return (
    <ModelOption
      variant="tertiary"
      icon={undefined}
      title={
        <Box className={classes.titleContainer}>
          <Box className={classes.withHelpIconContainer}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                id="models-label"
                variant="pMd"
                component="h3"
                bold
                className={cx({
                  [classes.disabled]: disabled,
                  [classes.active]: active,
                  [classes.inactive]: !active,
                })}
              >
                {'Legacy Studio - Global'}
              </Typography>
              <Tag variant="papaya">Retiring</Tag>
            </Box>
            {isUpgrade && <HelpIcon />}
          </Box>
          <Typography
            id="models-description"
            variant="pSm"
            className={cx({
              [classes.disabled]: disabled,
              [classes.active]: active,
              [classes.inactive]: !active,
            })}
          >
            {
              'Our original, simplified editor experience designed for global language voices.'
            }
          </Typography>
        </Box>
      }
      disabled={disabled}
      active={active}
    >
      <Box
        className={cx(classes.inputContainer, { [classes.disabled]: disabled })}
      >
        <SingleSelect
          ariaLabel="Select other language"
          value={selection}
          items={filteredOptions.map(o => ({ label: o.label, value: o.label }))}
          onChange={item => handleSelectionChange(item.label)}
          openLabel="Select"
          disabled={disabled}
          maxHeight="313px"
        />
        {showError && active && (
          <Typography variant="pSm" color="error" role="alert">
            Choose a language
          </Typography>
        )}
      </Box>
    </ModelOption>
  );
};

export default OtherLanguagesOption;
